import React, { useMemo } from "react";

import cn from "classnames";
import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import SelectableTeam from "@mapmycustomers/shared/types/ui/SelectableTeam";
import User from "@mapmycustomers/shared/types/User";
import { SearchField, TeamItem } from "@mapmycustomers/ui";

import useLowerCaseState from "@app/util/hook/useLowerCaseState";

import messages from "../messages";

import styles from "./TeamList.module.scss";

interface Props {
  className?: string;
  isCurrentUserOwner: boolean;
  isTeamSelected: (team: SelectableTeam) => boolean;
  noTeamUserIds: User["id"][];
  onTeamClick?: (team: SelectableTeam) => void;
  teams: Team[];
}

const TeamList = ({
  className,
  isCurrentUserOwner,
  isTeamSelected,
  noTeamUserIds,
  onTeamClick,
  teams,
}: Props) => {
  const intl = useIntl();

  const [teamQuery, setTeamQuery] = useLowerCaseState("");

  const filteredTeams = useMemo(
    () =>
      (teamQuery
        ? teams.filter(({ name }) => name.toLowerCase().includes(teamQuery))
        : teams
      ).filter(({ users }) => users.length),
    [teamQuery, teams]
  );
  const noTeamVisible =
    noTeamUserIds.length > 0 &&
    (!teamQuery || intl.formatMessage(messages.noTeam).toLowerCase().includes(teamQuery));
  const ownersTeamVisible =
    isCurrentUserOwner &&
    (!teamQuery || intl.formatMessage(messages.owners).toLowerCase().includes(teamQuery));

  return (
    <div className={cn(styles.container, className)}>
      <SearchField
        onChange={setTeamQuery}
        placeholder={intl.formatMessage(messages.searchTeamPlaceholder)}
        size="middle"
        value={teamQuery}
      />
      <div className={styles.list}>
        {ownersTeamVisible && (
          <TeamItem
            key="owners"
            onClick={onTeamClick}
            selected={isTeamSelected("owners")}
            team="owners"
          />
        )}
        {filteredTeams.map((team) => (
          <TeamItem
            key={team.id}
            onClick={onTeamClick}
            selected={isTeamSelected(team)}
            team={team}
          />
        ))}
        {noTeamVisible && (
          <TeamItem
            key="noTeam"
            onClick={onTeamClick}
            selected={isTeamSelected("noTeam")}
            team="noTeam"
          />
        )}
      </div>
    </div>
  );
};

export default TeamList;
